import { useCallback, useRef } from "react"

/**
 * Default minimum dimensions for resizing.
 */
const DEFAULT_MIN_DIMENSIONS = {
  width: 100,
  height: 50,
} as const

/**
 * Minimum dimensions interface
 */
export interface MinDimensions {
  width: number
  height: number
}

/**
 * Element update payload for dimension changes
 */
export interface ElementDimensionUpdate {
  width?: number
  height?: number
}

/**
 * Parameters for the useElementResizing hook
 */
export interface UseElementResizingParams {
  /** The ID of the element being resized */
  elementId: string
  /** The current width of the element (from element props) */
  currentWidth: number
  /** The current height of the element (from element props) */
  currentHeight: number
  /** Callback to update element's dimensions in the parent state */
  onUpdate: (elementId: string, updates: ElementDimensionUpdate) => void
  /** Optional callback to set a flag to skip other updates (e.g., config updates) during resize */
  setSkipUpdateFlag?: (skip: boolean) => void
  /** Optional minimum dimensions { width, height } */
  minDimensions?: MinDimensions
  /** Optional flag if aspect ratio should be maintained (primarily for corner resize) */
  maintainAspectRatio?: boolean
  /** Optional aspect ratio (height/width) if maintainAspectRatio is true. If not provided, calculated from initial dimensions */
  aspectRatio?: number
  /** Optional ref to the wrapper element for applying 'is-resizing-outline' class */
  wrapperRef?: React.RefObject<HTMLElement | null>
}

/**
 * Return type for useElementResizing hook
 */
export interface UseElementResizingReturn {
  handleHorizontalResizeStart: (e: React.MouseEvent) => void
  handleVerticalResizeStart: (e: React.MouseEvent) => void
  handleCornerResizeStart: (e: React.MouseEvent) => void
}

/**
 * Type for resize handle types
 */
type ResizeHandleType = "horizontal" | "vertical" | "corner"

/**
 * Custom hook to manage element resizing logic.
 *
 * @param params - Parameters for the hook
 * @returns An object containing resize handler functions
 *
 * @example
 * const { handleHorizontalResizeStart, handleVerticalResizeStart, handleCornerResizeStart } = useElementResizing({
 *   elementId: 'chart-1',
 *   currentWidth: 600,
 *   currentHeight: 400,
 *   onUpdate: (id, updates) => updateElement(id, updates),
 *   setSkipUpdateFlag: setSkipConfigUpdate,
 *   minDimensions: { width: 200, height: 150 },
 *   maintainAspectRatio: true,
 *   wrapperRef: elementRef,
 * })
 */
export const useElementResizing = ({
  elementId,
  currentWidth,
  currentHeight,
  onUpdate,
  setSkipUpdateFlag,
  minDimensions = DEFAULT_MIN_DIMENSIONS,
  maintainAspectRatio = false,
  aspectRatio: initialAspectRatio,
  wrapperRef,
}: UseElementResizingParams): UseElementResizingReturn => {
  const isResizingRef = useRef<boolean>(false) // Internal ref to track active resizing

  const startResize = useCallback(
    (e: React.MouseEvent, handleType: ResizeHandleType): void => {
      e.preventDefault()
      e.stopPropagation()

      if (isResizingRef.current) return // Prevent multiple resize initiations
      isResizingRef.current = true

      if (setSkipUpdateFlag) {
        setSkipUpdateFlag(true)
      }
      if (wrapperRef?.current) {
        wrapperRef.current.style.userSelect = "none"
        wrapperRef.current.classList.add("is-resizing-outline")
      }

      const startX = e.clientX
      const startY = e.clientY
      const startW = currentWidth
      const startH = currentHeight

      const calculatedAspectRatio =
        initialAspectRatio || (startW > 0 ? startH / startW : 1)

      const handleMouseMove = (moveEvent: MouseEvent): void => {
        moveEvent.preventDefault()
        moveEvent.stopPropagation()

        const deltaX = moveEvent.clientX - startX
        const deltaY = moveEvent.clientY - startY

        let newWidth = startW
        let newHeight = startH

        if (handleType === "horizontal" || handleType === "corner") {
          newWidth = Math.max(minDimensions.width, startW + deltaX)
        }

        if (handleType === "vertical" || handleType === "corner") {
          newHeight = Math.max(minDimensions.height, startH + deltaY)
        }

        if (maintainAspectRatio && handleType === "corner") {
          // Adjust height based on width change to maintain aspect ratio
          // Or adjust width based on height change, prioritizing larger delta
          if (Math.abs(deltaX) > Math.abs(deltaY)) {
            newHeight = newWidth * calculatedAspectRatio
          } else {
            newWidth = newHeight / calculatedAspectRatio
          }
          // Re-check min dimensions after aspect ratio adjustment
          newWidth = Math.max(minDimensions.width, newWidth)
          newHeight = Math.max(minDimensions.height, newHeight)

          // If one dimension caused the other to go below min, adjust both
          if (newWidth * calculatedAspectRatio < minDimensions.height) {
            newHeight = minDimensions.height
            newWidth = newHeight / calculatedAspectRatio
          }
          if (newHeight / calculatedAspectRatio < minDimensions.width) {
            newWidth = minDimensions.width
            newHeight = newWidth * calculatedAspectRatio
          }
        }

        const updatePayload: ElementDimensionUpdate = {}
        if (newWidth !== currentWidth) updatePayload.width = newWidth
        if (newHeight !== currentHeight) updatePayload.height = newHeight

        if (Object.keys(updatePayload).length > 0) {
          onUpdate(elementId, updatePayload)
        }
      }

      const handleMouseUp = (): void => {
        isResizingRef.current = false
        if (setSkipUpdateFlag) {
          setSkipUpdateFlag(false)
        }
        if (wrapperRef?.current) {
          wrapperRef.current.style.removeProperty("user-select")
          wrapperRef.current.classList.remove("is-resizing-outline")
        }
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
      }

      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
    },
    [
      elementId,
      currentWidth,
      currentHeight,
      onUpdate,
      setSkipUpdateFlag,
      minDimensions,
      maintainAspectRatio,
      initialAspectRatio,
      wrapperRef,
    ]
  )

  const handleHorizontalResizeStart = useCallback(
    (e: React.MouseEvent): void => startResize(e, "horizontal"),
    [startResize]
  )

  const handleVerticalResizeStart = useCallback(
    (e: React.MouseEvent): void => startResize(e, "vertical"),
    [startResize]
  )

  const handleCornerResizeStart = useCallback(
    (e: React.MouseEvent): void => startResize(e, "corner"),
    [startResize]
  )

  return {
    handleHorizontalResizeStart,
    handleVerticalResizeStart,
    handleCornerResizeStart,
  }
}
